import { LitElement, html, css } from "lit";
import { customElement, property } from "lit/decorators.js";

/**
 * bn-spinner is the loading indicator: a celeste-tinted ring. Set `size` in px
 * (default 20) and optionally a `label` for an accessible loading row. Centers
 * itself when `block` is set (for full-panel loading states).
 */
@customElement("bn-spinner")
export class BnSpinner extends LitElement {
  @property({ type: Number }) size = 20;
  @property() label = "";
  @property({ type: Boolean }) block = false;

  static override styles = css`
    :host {
      display: inline-flex;
      align-items: center;
      gap: var(--bino-space-sm);
      color: var(--bino-text-secondary);
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-sm);
    }
    :host([block]) {
      display: flex;
      justify-content: center;
      padding: var(--bino-space-xl);
    }
    .ring {
      border-radius: 50%;
      border-style: solid;
      border-color: var(--bino-border-light);
      border-top-color: var(--bino-primary);
      animation: spin 0.7s linear infinite;
    }
    @keyframes spin {
      to {
        transform: rotate(360deg);
      }
    }
    @media (prefers-reduced-motion: reduce) {
      .ring {
        animation-duration: 1.6s;
      }
    }
  `;

  override render() {
    const px = `${this.size}px`;
    const border = `${Math.max(2, Math.round(this.size / 10))}px`;
    return html`
      <span
        class="ring"
        role="progressbar"
        aria-label=${this.label || "Loading"}
        style="width:${px};height:${px};border-width:${border}"
      ></span>
      ${this.label ? html`<span>${this.label}</span>` : ""}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bn-spinner": BnSpinner;
  }
}
